/**
 * Main Funnel Component - Orchestrates the entire funnel flow
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FunnelConfig } from '../config/types';
import { ThemeProvider } from '../hooks/use-theme';
import { FunnelConfigProvider } from '../hooks/use-funnel-config';
import { applyTheme } from '../config/theme';
import { FunnelHero } from './FunnelHero';
import { createBookingProvider } from '../services/booking-providers';
import { executeSuccessActions } from '../services/success-actions';

interface FunnelProps {
  config: FunnelConfig;
}

export function Funnel({ config }: FunnelProps) {
  // useNavigate must be called unconditionally at the top level
  // It requires a Router context (BrowserRouter/HashRouter) to be present
  const navigate = useNavigate();
  const [quizStarted, setQuizStarted] = useState(false);

  // Apply theme on mount
  useEffect(() => {
    applyTheme(config.theme);
  }, [config.theme]);

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setTimeout(() => {
      const quizElement = document.getElementById('inline-quiz');
      if (quizElement) {
        quizElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleQuizComplete = async (formData: Record<string, any>, contactId?: string) => {
    if (!navigate) {
      console.error('Navigation not available - Router context missing');
      return;
    }

    try {
      // Navigate to booking page based on booking provider type
      if (config.booking.type === 'calcom') {
        // For Cal.com, navigate to booking page with province info
        navigate('/book', {
          state: {
            formData,
            contactId,
            province: formData.province, // Pass province for routing
          },
        });
      } else if (config.booking.type === 'ghl-caas') {
        // For GHL+CaaS, use the existing route structure
        const bookingRoute = `/book/${contactId || 'new'}?agencyId=${(config.booking as any).agencyId}`;
        navigate(bookingRoute, {
          state: {
            formData,
            contactId,
          },
        });
      } else {
        // Custom booking - navigate to generic booking page
        navigate('/book', {
          state: {
            formData,
            contactId,
          },
        });
      }
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback to window.location if navigate fails
      if (typeof window !== 'undefined') {
        window.location.href = '/book';
      }
    }
  };

  return (
    <ThemeProvider theme={config.theme}>
      <FunnelConfigProvider config={config}>
        <div className="min-h-screen bg-slate-900">
          <FunnelHero
            onStartQuiz={handleStartQuiz}
            quizStarted={quizStarted}
            onQuizComplete={handleQuizComplete}
          />
        </div>
      </FunnelConfigProvider>
    </ThemeProvider>
  );
}

/**
 * Hook for handling booking completion
 */
export async function handleBookingComplete(
  config: FunnelConfig,
  context: {
    contactId?: string;
    bookingId?: string;
    appointmentId?: string;
    formData: Record<string, any>;
    bookingData?: Record<string, any>;
  }
): Promise<{ route?: string; component?: React.ComponentType<any> }> {
  return executeSuccessActions(config.successActions, {
    ...context,
    ghlConfig: config.ghl,
  });
}
